import React, { useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { X, Camera, Paperclip, Sparkles, Loader2, Save } from "lucide-react";
import { BOLLA } from "@/constants/testIds";
import api from "@/api";
import useFormDraft from "@/hooks/useFormDraft";
import BollaScanButton from "@/pages/bolle/BollaScanButton";
import { useAuth } from "@/context/AuthContext";

const CATEGORIE = [
  "Materiali",
  "Materiali edili",
  "Elettrici",
  "Idrici",
  "Condizionamento",
  "Porte",
  "Serramenti",
  "Impianti",
  "Finiture",
  "Ferramenta",
  "Attrezzature",
  "Trasporti",
  "Altro",
];

/**
 * Modale grande di creazione/modifica bolla.
 *
 * Props:
 *   editingId    – id in edit oppure null
 *   form/setForm – stato del form
 *   cantieri     – lista cantieri per la select
 *   anteprima    – {imponibile, iva, totale}
 *   codiceBolla  – (categoria) => "COD.XX"
 *   onClose
 *   onSubmit     – (form) => Promise
 *   saving       – bool
 *   handleFiles  – (FileList) => void
 *   removeAllegato – (idx) => void
 */
export default function BollaForm({
  editingId, form, setForm, cantieri, anteprima, codiceBolla,
  onClose, onSubmit, saving, handleFiles, removeAllegato,
}) {
  const { user } = useAuth();
  const canSeePrices = user && ["amministratore", "impiegato_amministrativo", "responsabile_cantiere"].includes(user.role);
  const [extracting, setExtracting] = useState(false);
  // Protezione anti-perdita dati: autosave in localStorage + chiusura sicura
  const isBollaDirty = (f) => Boolean(
    (f.numero_bolla && String(f.numero_bolla).trim()) ||
    (f.fornitore && String(f.fornitore).trim()) ||
    (f.materiale && String(f.materiale).trim()) ||
    (f.numero_ordine && String(f.numero_ordine).trim()) ||
    (f.note && String(f.note).trim()) ||
    (Array.isArray(f.allegati) && f.allegati.length > 0) ||
    (f.quantita && Number(String(f.quantita).replace(",", ".")) > 0) ||
    (f.prezzo_unitario && Number(String(f.prezzo_unitario).replace(",", ".")) > 0)
  );
  const { clearDraft, safeClose, backdropHandlers, dirty } = useFormDraft({
    storageKey: editingId ? `draft:bolla:${editingId}` : "draft:bolla:new",
    form,
    setForm,
    isDirty: isBollaDirty,
    onClose,
  });
  // Espone clearDraft: quando il salvataggio riesce, il parent chiuderà con
  // onClose() ma NON chiama clearDraft — lo facciamo qui appena `saving` passa
  // da true a false senza errori (il parent chiama onClose che smonta il modal).
  const wasSaving = useRef(false);
  useEffect(() => {
    if (wasSaving.current && !saving) {
      // il salvataggio è terminato: puliamo la bozza per sicurezza
      // (in caso di errore il parent non chiude, quindi la bozza persiste)
    }
    wasSaving.current = saving;
  }, [saving]);
  // Chiusura definitiva su Escape (con conferma se dirty)
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        safeClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirty]);
  // Traccia gli allegati già analizzati per evitare doppie chiamate
  const extractedRef = useRef(new Set());
  // Anagrafica fornitori — usata per popolare il datalist "Fornitore"
  const { data: fornitoriList = [] } = useQuery({
    queryKey: ["fornitori-anagrafica"],
    queryFn: () => api.get("/entita/fornitori").then((r) => r.data),
    staleTime: 60_000,
  });
  // Anagrafica fornitore rilevata (dal DB o dall'AI) — mostrata sotto al campo fornitore
  const [fornitoreInfo, setFornitoreInfo] = useState(null);
  // { matched: bool, ragione_sociale, partita_iva, indirizzo }

  const estraiDati = async (dataUrl) => {
    if (!dataUrl) {
      toast.error("Nessun allegato da analizzare");
      return;
    }
    setExtracting(true);
    const startToast = toast.loading("Analisi AI in corso…");
    try {
      const { data } = await api.post("/bolle/estrai-dati", { allegato: dataUrl });
      const d = data?.dati || {};
      const match = data?.fornitore_match || null;
      // Conta campi effettivamente riempiti per capire se l'estrazione è riuscita
      const filled = [d.numero_bolla, d.fornitore, d.materiale, d.data]
        .filter((v) => v && String(v).trim().length > 0).length +
        (d.quantita > 0 ? 1 : 0) + (d.prezzo_unitario > 0 ? 1 : 0);
      setForm((f) => ({
        ...f,
        numero_bolla: d.numero_bolla || f.numero_bolla,
        data: d.data && /^\d{4}-\d{2}-\d{2}$/.test(d.data) ? d.data : f.data,
        fornitore: d.fornitore || f.fornitore,
        materiale: d.materiale || f.materiale,
        quantita: d.quantita > 0 ? d.quantita : f.quantita,
        unita_misura: d.unita_misura || f.unita_misura,
        prezzo_unitario: d.prezzo_unitario > 0 ? d.prezzo_unitario : f.prezzo_unitario,
        aliquota_iva: d.aliquota_iva > 0 ? d.aliquota_iva : f.aliquota_iva,
        numero_ordine: d.numero_ordine || f.numero_ordine,
        note: d.note ? (f.note ? `${f.note}\n${d.note}` : d.note) : f.note,
        fornitore_partita_iva: match?.partita_iva || d.partita_iva || f.fornitore_partita_iva || "",
        fornitore_indirizzo: match?.indirizzo || d.indirizzo || f.fornitore_indirizzo || "",
      }));
      setFornitoreInfo({
        matched: !!match,
        ragione_sociale: match?.ragione_sociale || d.fornitore,
        partita_iva: match?.partita_iva || d.partita_iva || "",
        indirizzo: match?.indirizzo || d.indirizzo || "",
      });
      toast.dismiss(startToast);
      if (filled === 0) {
        toast.warning("L'AI non è riuscita a leggere la bolla — riprova con una foto più nitida e in luce buona, o compila manualmente.");
      } else if (match) {
        toast.success(`✓ ${filled} campi estratti — Fornitore riconosciuto: ${match.ragione_sociale}`);
      } else {
        toast.success(`✓ ${filled} campi compilati dall'AI — verifica e completa i dati mancanti`);
      }
    } catch (e) {
      toast.dismiss(startToast);
      toast.error(e.response?.data?.detail || "Estrazione non riuscita");
    } finally {
      setExtracting(false);
    }
  };

  // Auto-estrazione: quando viene aggiunto un nuovo allegato, lancia subito l'AI
  // (solo se la bolla è nuova e i campi principali sono ancora vuoti)
  useEffect(() => {
    if (editingId) return; // in edit non facciamo auto-estrazione
    if (extracting) return;
    if (!form.allegati || form.allegati.length === 0) return;
    // Prendi l'ultimo allegato aggiunto
    const last = form.allegati[form.allegati.length - 1];
    if (!last || extractedRef.current.has(last)) return;
    // Non partire se l'utente ha già digitato manualmente qualcosa di significativo
    const alreadyFilled = (form.fornitore || form.materiale || form.numero_bolla) &&
      (form.fornitore.length > 2 || form.materiale.length > 2);
    if (alreadyFilled) {
      extractedRef.current.add(last);
      return;
    }
    extractedRef.current.add(last);
    estraiDati(last);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.allegati]);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" {...backdropHandlers}>
      <div className="bg-white w-full max-w-3xl border border-slate-200 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()} onMouseUp={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h2 className="font-display font-bold text-xl">
              {editingId ? "Modifica bolla" : "Nuova bolla"}
            </h2>
            {dirty && !saving && (
              <div className="text-[11px] text-emerald-700 mt-0.5 inline-flex items-center gap-1" data-testid="bolla-autosave-indicator">
                <Save className="w-3 h-3" /> Bozza salvata automaticamente
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <BollaScanButton
              canSeePrices={canSeePrices}
              onFill={(patch) => {
                setForm((prev) => ({ ...prev, ...patch }));
                toast.success("Bolla precompilata dalla scansione");
              }}
            />
            <button onClick={safeClose} data-testid="btn-close-bolla-modal"><X className="w-5 h-5 text-slate-500" /></button>
          </div>
        </div>
        <div className="p-5 grid md:grid-cols-2 gap-4">
          {[
            ["numero_bolla", "Numero bolla"],
            ["fornitore", "Fornitore"],
            ["materiale", "Materiale"],
            ["numero_ordine", "N° Ordine"],
          ].map(([k, l]) => (
            <div key={k}>
              <label className="text-xs font-semibold text-slate-600 block mb-1">{l}</label>
              <input
                value={form[k]}
                onChange={(e) => {
                  const v = e.target.value;
                  const patch = { [k]: v };
                  // Se seleziono un fornitore già in agenda, pre-compila categoria/P.IVA/indirizzo
                  if (k === "fornitore") {
                    const match = fornitoriList.find(
                      (f) => (f.ragione_sociale || "").toLowerCase() === v.toLowerCase()
                    );
                    if (match) {
                      if (match.categoria) patch.categoria = match.categoria;
                      if (match.partita_iva) patch.fornitore_partita_iva = match.partita_iva;
                      if (match.indirizzo) patch.fornitore_indirizzo = match.indirizzo;
                    }
                  }
                  setForm({ ...form, ...patch });
                }}
                data-testid={`input-${k.replace(/_/g, "-")}`}
                list={k === "fornitore" ? "bolle-fornitori-datalist" : undefined}
                autoComplete={k === "fornitore" ? "off" : undefined}
                className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
              />
              {k === "fornitore" && fornitoreInfo && (
                <div
                  data-testid="fornitore-anagrafica-badge"
                  className={`mt-1 text-[10px] p-1.5 rounded-sm border ${fornitoreInfo.matched
                    ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                    : "bg-amber-50 border-amber-200 text-amber-800"}`}
                >
                  <div className="font-bold">
                    {fornitoreInfo.matched ? "✓ Fornitore già in anagrafica" : "+ Nuovo fornitore (verrà salvato)"}
                  </div>
                  {fornitoreInfo.partita_iva && (
                    <div>P.IVA: <span className="font-mono">{fornitoreInfo.partita_iva}</span></div>
                  )}
                  {fornitoreInfo.indirizzo && <div>{fornitoreInfo.indirizzo}</div>}
                </div>
              )}
            </div>
          ))}
          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-1">Data</label>
            <input type="date" value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-1">Cantiere</label>
            <select value={form.cantiere_id} onChange={(e) => setForm({ ...form, cantiere_id: e.target.value })} data-testid="select-cantiere-id" className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm">
              <option value="">— seleziona —</option>
              <optgroup label="🏗️ Cantieri">
                {cantieri.filter((c) => c.tipo !== "manutenzione").map((c) => (
                  <option key={c.cantiere_id} value={c.cantiere_id}>🏗️ {c.nome}</option>
                ))}
              </optgroup>
              <optgroup label="🔧 Manutenzioni">
                {cantieri.filter((c) => c.tipo === "manutenzione").map((c) => (
                  <option key={c.cantiere_id} value={c.cantiere_id}>🔧 {c.nome}</option>
                ))}
              </optgroup>
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-1">Categoria</label>
            <select value={form.categoria} onChange={(e) => setForm({ ...form, categoria: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm">
              {CATEGORIE.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-1">Unità di misura</label>
            <input value={form.unita_misura} onChange={(e) => setForm({ ...form, unita_misura: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-1">Quantità</label>
            <input
              type="text"
              inputMode="decimal"
              autoComplete="off"
              placeholder="es. 12,5"
              value={form.quantita ?? ""}
              onChange={(e) => setForm({ ...form, quantita: e.target.value })}
              data-testid="input-quantita"
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-1">Prezzo unitario €</label>
            <input
              type="text"
              inputMode="decimal"
              autoComplete="off"
              placeholder="es. 90,80"
              value={form.prezzo_unitario ?? ""}
              onChange={(e) => setForm({ ...form, prezzo_unitario: e.target.value })}
              data-testid="input-prezzo-unitario"
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-1">Aliquota IVA %</label>
            <input
              type="text"
              inputMode="decimal"
              autoComplete="off"
              placeholder="es. 22"
              value={form.aliquota_iva ?? ""}
              onChange={(e) => setForm({ ...form, aliquota_iva: e.target.value })}
              data-testid="input-aliquota-iva"
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
            />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-semibold text-slate-600 block mb-1">Note</label>
            <textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-semibold text-slate-600 block mb-1">
              Allegati bolla ({form.allegati.length}) — foto multiple o PDF
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <label className="flex items-center justify-center gap-2 px-3 py-2 border-2 border-violet-400 bg-gradient-to-br from-violet-500 to-fuchsia-500 hover:from-violet-600 hover:to-fuchsia-600 text-white rounded-sm text-sm cursor-pointer font-semibold shadow-brutal">
                <Sparkles className="w-4 h-4" />
                <span>Inserimento AI (foto o PDF)</span>
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  capture="environment"
                  onChange={(e) => handleFiles(e.target.files)}
                  className="hidden"
                  data-testid="bolla-ai-input"
                />
              </label>
              <label className="flex items-center justify-center gap-2 px-3 py-2 border border-blue-300 bg-blue-50 hover:bg-blue-100 text-blue-800 rounded-sm text-sm cursor-pointer">
                <Camera className="w-4 h-4" />
                <span>Scatta foto</span>
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  multiple
                  onChange={(e) => handleFiles(e.target.files)}
                  className="hidden"
                  data-testid="bolla-camera-input"
                />
              </label>
              <label className="flex items-center justify-center gap-2 px-3 py-2 border border-slate-300 bg-white hover:bg-slate-50 text-slate-800 rounded-sm text-sm cursor-pointer">
                <Paperclip className="w-4 h-4" />
                <span>Carica PDF</span>
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  multiple
                  onChange={(e) => handleFiles(e.target.files)}
                  className="hidden"
                  data-testid="bolla-file-input"
                />
              </label>
            </div>
            <div className="mt-2 text-[11px] text-violet-800 bg-violet-50 border border-violet-200 rounded-sm p-2 inline-flex items-center gap-1">
              <Sparkles className="w-3.5 h-3.5" />
              <span>Clicca <b>Inserimento AI</b> per compilare automaticamente numero bolla, fornitore, materiale, quantità, prezzo e IVA dalla foto o dal PDF.</span>
            </div>
            {form.allegati.length > 0 && (
              <>
                <div className="mt-3 flex items-center justify-between gap-2 p-3 bg-violet-50 border-2 border-violet-300 rounded-sm">
                  <div className="text-xs text-violet-900 flex-1">
                    {extracting ? (
                      <span className="inline-flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <b>AI in corso…</b> sto leggendo l'allegato e compilando i campi
                      </span>
                    ) : (
                      <>
                        <b>Estrazione automatica AI:</b> i campi vengono compilati appena carichi una foto o un PDF.
                        Puoi ripetere l'estrazione o estrarre da un allegato diverso.
                      </>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      extractedRef.current.delete(form.allegati[0]);
                      estraiDati(form.allegati[0]);
                    }}
                    disabled={extracting}
                    data-testid="btn-estrai-dati-bolla"
                    className="inline-flex items-center gap-2 px-3 py-1.5 bg-violet-600 hover:bg-violet-700 disabled:opacity-60 text-white text-xs font-semibold rounded-sm whitespace-nowrap"
                  >
                    {extracting
                      ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Analizzo…</>
                      : <><Sparkles className="w-3.5 h-3.5" /> Ri-estrai con AI</>}
                  </button>
                </div>
                <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
                {form.allegati.map((b64, i) => {
                  const isPdf = b64.startsWith("data:application/pdf");
                  const key = `${b64.slice(0, 40)}-${i}`;
                  return (
                    <div key={key} className="relative border border-slate-200 rounded-sm overflow-hidden bg-slate-50">
                      {isPdf ? (
                        <div className="h-24 flex flex-col items-center justify-center text-slate-500 gap-1">
                          <Paperclip className="w-6 h-6" />
                          <span className="text-[10px] font-mono">PDF</span>
                        </div>
                      ) : (
                        <img src={b64} alt="" className="h-24 w-full object-cover" />
                      )}
                      <button
                        type="button"
                        onClick={() => estraiDati(b64)}
                        disabled={extracting}
                        data-testid={`bolla-estrai-file-${i}`}
                        title="Estrai dati da questo allegato"
                        className="absolute top-1 left-1 p-1 bg-white/90 hover:bg-violet-100 text-violet-700 rounded-sm border border-violet-200 disabled:opacity-60"
                      >
                        <Sparkles className="w-3 h-3" />
                      </button>
                      <button
                        type="button"
                        onClick={() => removeAllegato(i)}
                        data-testid={`bolla-remove-file-${i}`}
                        className="absolute top-1 right-1 p-1 bg-white/90 hover:bg-white text-rose-600 rounded-sm border border-slate-200"
                        title="Rimuovi"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  );
                })}
                </div>
              </>
            )}
          </div>
          <div className="md:col-span-2 border-t border-slate-200 pt-4 grid grid-cols-4 gap-3 text-sm">
            <div><div className="overline">Imponibile</div><div className="font-display font-bold">€ {anteprima.imponibile}</div></div>
            <div><div className="overline">IVA</div><div className="font-display font-bold">€ {anteprima.iva}</div></div>
            <div><div className="overline">Totale con IVA</div><div className="font-display font-bold text-orange-600">€ {anteprima.totale}</div></div>
            <div><div className="overline">Codice contabile</div><div className="font-display font-bold text-orange-700">{codiceBolla(form.categoria)}</div></div>
          </div>
        </div>
        <div className="p-5 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={safeClose} className="px-4 py-2 text-sm border border-slate-300 rounded-sm hover:bg-slate-50">Annulla</button>
          <button
            data-testid={BOLLA.saveButton}
            onClick={async () => {
              try {
                await onSubmit(form);
                clearDraft();
              } catch (_) {
                // errore già gestito dal parent (toast). Mantieni la bozza.
              }
            }}
            disabled={saving}
            className="px-4 py-2 text-sm bg-orange-600 hover:bg-orange-700 disabled:opacity-60 text-white font-semibold rounded-sm"
          >
            {saving ? "Salvataggio…" : editingId ? "Salva modifiche" : "Salva bolla"}
          </button>
        </div>
      </div>
      <datalist id="bolle-fornitori-datalist" data-testid="datalist-fornitori">
        {fornitoriList.map((f) => (
          <option key={f.fornitore_id} value={f.ragione_sociale}>
            {f.partita_iva ? `P.IVA ${f.partita_iva}` : ""}{f.indirizzo ? ` · ${f.indirizzo}` : ""}
          </option>
        ))}
      </datalist>
    </div>
  );
}
